/**
 * /app/trusts/$id/tax — Tax position, trust-scoped.
 *
 * Phase 3 Task 3.4b (2026-05-17) wired this screen to
 * `useTrustTaxCertificates`. The previous fixture data (a hardcoded
 * beneficiary IT3(t) row + a settlor-attribution sub-headline naming a
 * specific person) was deleted.
 *
 * 2026-08-06: the two static "ITR12T submission" / "Provisional tax (IRP6)"
 * panels stated the SARS deadlines as prose and nothing counted down to them.
 * They are now `StatutoryCalendarCard`, which computes the same dates live
 * against today. Still no persona PII — the calendar is statute, not trust data.
 *
 * 2026-08-22: `useCreateTaxCertificate` existed but nothing on the product
 * called it, so this register was structurally permanently empty — the copy
 * promised an "in-platform tax module" no path could ever fill. Added the
 * "Issue tax certificate" affordance (role-gated the same as the rest of the
 * app's admin write surfaces; see `TAX_CERTIFICATE_ROLES` below) that drafts
 * a row via POST /v1/trusts/{trustId}/tax/certificates.
 */

import { useState, type CSSProperties } from 'react';
import { displayPersonName } from '../../../../lib/format/person-name';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { ScreenShell } from '../../../../components/screens/screen-shell';
import { TrustTabs } from '../../../../components/screens/trust-tabs';
import { Icon } from '../../../../components/icon';
import { StatutoryCalendarCard } from '../../../../components/statutory-calendar-card';
import {
  useTrust,
  useTrustTaxCertificates,
  useCreateTaxCertificate,
  useCanActOnTrust,
  useTrustBeneficiaries,
  type TaxCertificate,
  type TaxCertificateType,
  type BeneficiaryRow,
  type TrustSummary,
} from '../../../../lib/api';
import { useSession } from '../../../../lib/use-session';
import { useToastStore } from '../../../../lib/toast-store';
import { mapError } from '../../../../lib/error-messages';
import { labelForStatus } from '../../../../lib/labels';
import { formatRand } from '../../../../lib/fmt-r';
import { formatAsAtShort } from '../../../../components/value-provenance';
// -- COHESION PASS, PHASE 5 (2026-09-15) ------------------------------------
import { ConsoleBand } from '../../../../components/admin/console-band';
import { BleedTable, type BleedColumn, type BleedRow } from '../../../../components/bleed-table';
import { StateCard } from '../../../../components/state-card';
import { Skeleton } from '../../../../components/ui/skeleton';

export const Route = createFileRoute('/app/trusts/$id/tax')({
  component: TaxScreen,
});

// 2026-05-20 (Batch 8 / F02-P-074): collapsed onto the shared formatRand
// from lib/fmt-r so every Rand cell uses the same narrow-space thousands
// separator and NBSP between R and the number.
function fmtR(n: number) {
  return formatRand(n);
}

/** Sentence case, on the one chip shape, reading its fills from the status
 *  tokens. An issued certificate is a good outcome; everything else is
 *  information, and none of it is a warning. */
function CertStatusPill({ status }: { status: TaxCertificate['status'] }) {
  const tone = status === 'issued' ? 'trust-chip--positive' : 'trust-chip--info';
  return <span className={`trust-chip ${tone}`}>{labelForStatus(status)}</span>;
}

/** The register columns. The status is the trailing column and carries a chip,
 *  not a button, so it is a plain column: the contract reserves the
 *  right-aligned action column for a control, and a right-aligned header over a
 *  left-aligned chip is one of the two things review called broken. */
const CERT_COLUMNS: BleedColumn[] = [
  { key: 'beneficiary', header: 'Beneficiary' },
  { key: 'year', header: 'Tax year', kind: 'num' },
  { key: 'type', header: 'Type' },
  { key: 'amount', header: 'Amount', kind: 'num' },
  { key: 'status', header: 'Status' },
];

/** CELL-LEVEL LOADING, per the contract: one inline skeleton bar where each
 *  value will be, inside the real table, so the header and the column widths
 *  are already correct when the rows arrive. By construction it shows no digit,
 *  which is design gate A2 -- a zero amount on a tax certificate is not a
 *  placeholder, it is a claim. */
function certSkeletonRow(n: number): BleedRow {
  return {
    key: `cert-skeleton-${n}`,
    cells: {
      beneficiary: <Skeleton width={200} height={13} radius={4} />,
      year: <Skeleton width={56} height={13} radius={4} />,
      type: <Skeleton width={60} height={13} radius={4} />,
      amount: <Skeleton width={90} height={13} radius={4} />,
      status: <Skeleton width={72} height={13} radius={4} />,
    },
  };
}

/** One certificate. The beneficiary is a person, so italic serif; the amount is
 *  an amount, so roman with tabular figures. */
function certRow(cert: TaxCertificate): BleedRow {
  return {
    key: cert.id,
    cells: {
      beneficiary: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: 'var(--surface-2)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--ink-2)',
              flexShrink: 0,
            }}
          >
            <Icon name="doc" size={14} />
          </span>
          <span className="bleed-table-entity">{displayPersonName(cert.beneficiaryName)}</span>
        </span>
      ),
      year: <span className="bleed-table-mono">{cert.taxYear}</span>,
      type: <span className="trust-chip trust-chip--info">{cert.certificateType}</span>,
      amount: <span className="trust-table-figure">{fmtR(cert.amount)}</span>,
      status: <CertStatusPill status={cert.status} />,
    },
  };
}

// Mirrors the backend's FIRM_STAFF_MEMBER_ROLES gate on
// POST /v1/trusts/{trustId}/tax/certificates (trusts-tax-certificate-create.ts
// — isFirmStaffMember: admin/compliance/director/rm/super_admin/superadmin).
// Combined with the member-tier gate (useCanActOnTrust) below so a
// beneficiary-tier member never sees it either. A non-staff caller never gets
// the affordance; the backend would 403 ('TAX_CERTIFICATE_ADMIN_REQUIRED')
// and showing a button that always fails would be dishonest.
const TAX_CERTIFICATE_ROLES = new Set([
  'admin',
  'compliance',
  'director',
  'rm',
  'super_admin',
  'superadmin',
]);

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <div style={{ marginTop: 4, fontSize: 12, color: 'var(--negative)' }}>{msg}</div>;
}

const certFieldStyle: CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 8,
  border: '1px solid var(--hairline-2)', background: 'var(--bg)',
  color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 13.5,
  outline: 'none', boxSizing: 'border-box',
};

interface NewCertValues {
  beneficiaryId: string;
  certificateType: TaxCertificateType;
  taxYear: string;
  amountRand: string;
}

// A SARS year of assessment is named for the calendar year its Feb year end
// falls in — from 1 March it is already the NEXT one. Mirrors the same
// month-2 (March) cutover statutory-calendar.ts uses for `currentYoa`.
function currentTaxYear(): number {
  const now = new Date();
  return now.getUTCMonth() >= 2 ? now.getUTCFullYear() + 1 : now.getUTCFullYear();
}

function newCertEmpty(): NewCertValues {
  return {
    beneficiaryId: '',
    certificateType: 'IT3(t)',
    taxYear: String(currentTaxYear()),
    amountRand: '',
  };
}

/**
 * "Issue tax certificate" — drafts an IT3(t)/IT3(b) via
 * POST /v1/trusts/{trustId}/tax/certificates. Field shape mirrors the
 * backend's CreateTaxCertificateSchema exactly (trusts-tax-certificate-create.ts):
 * beneficiaryId (must already exist on this trust — 404 otherwise), taxYear
 * (int 1900..2200), certificateType, amount (whole ZAR, non-negative int —
 * NOT cents, unlike distributions). Issues a `draft` row; the separate
 * "generate PDF / apply SARS reference" step is a different handler this
 * modal does not call. On success the register refreshes: useCreateTaxCertificate
 * invalidates the ['trusts','tax','certificates',trustId] query, which is a
 * prefix of this screen's own query key, so certsQuery refetches automatically.
 */
function IssueTaxCertificateModal({
  trustId,
  onClose,
}: {
  trustId: string;
  onClose: () => void;
}) {
  const pushToast = useToastStore((s) => s.pushToast);
  // Mounted only while the modal is open, so this query fires on demand —
  // same idiom as the beneficiary/team pickers in the distributions "New
  // distribution" modal.
  const beneficiariesQ = useTrustBeneficiaries({ trustId });
  const create = useCreateTaxCertificate();

  const [values, setValues] = useState<NewCertValues>(newCertEmpty());
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const beneficiaries: BeneficiaryRow[] = (beneficiariesQ.data?.beneficiaries ?? []).filter(
    (b) => b.status === 'active',
  );

  function set<K extends keyof NewCertValues>(field: K, val: NewCertValues[K]) {
    setValues((prev) => ({ ...prev, [field]: val }));
  }
  function touch(field: keyof NewCertValues) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }
  function touchAll() {
    setTouched(Object.fromEntries(Object.keys(newCertEmpty()).map((k) => [k, true])));
  }

  // ── Validation — mirrors CreateTaxCertificateSchema ─────────────────────
  const taxYearTrimmed = values.taxYear.trim();
  const taxYearNum = /^\d+$/.test(taxYearTrimmed) ? Number(taxYearTrimmed) : null;
  const amountTrimmed = values.amountRand.trim();
  const amountNum = /^\d+$/.test(amountTrimmed) ? Number(amountTrimmed) : null;

  const errors: Partial<Record<keyof NewCertValues, string>> = {};
  if (!values.beneficiaryId) {
    errors.beneficiaryId = 'Select a beneficiary';
  }
  if (taxYearNum === null || taxYearNum < 1900 || taxYearNum > 2200) {
    errors.taxYear = 'Tax year must be a whole number between 1900 and 2200';
  }
  if (amountNum === null) {
    errors.amountRand = 'Amount is required: a whole Rand value (e.g. 12000)';
  }

  const isValid = Object.keys(errors).length === 0;
  const err = (field: keyof NewCertValues) => (touched[field] ? errors[field] : undefined);

  function handleSubmit() {
    touchAll();
    if (!isValid || taxYearNum === null || amountNum === null || create.isPending) return;
    create.mutate(
      {
        trustId,
        beneficiaryId: values.beneficiaryId,
        taxYear: taxYearNum,
        certificateType: values.certificateType,
        amount: amountNum,
      },
      {
        onSuccess: (res) => {
          pushToast({
            kind: 'success',
            message: `${res.certificate.certificateType} drafted for ${displayPersonName(res.certificate.beneficiaryName)} (${fmtR(res.certificate.amount)}). Status: ${res.certificate.status}.`,
          });
          onClose();
        },
        onError: (apiErr) => {
          if (apiErr.isStepUpRequired) return; // global interceptor redirects
          pushToast({ kind: 'error', message: mapError(apiErr, 'save') });
        },
      },
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Issue tax certificate"
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="card"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
      >
        <div className="card-head">
          <h3 className="card-title" style={{ fontSize: 18 }}>
            Issue a tax certificate
          </h3>
        </div>
        <p style={{ margin: '4px 0 14px', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
          Drafts an IT3(t) or IT3(b) for a beneficiary of this trust.
          Generating the PDF and applying a SARS submission number is a
          separate step.
        </p>

        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 500 }}>Beneficiary</span>
          {beneficiariesQ.isLoading ? (
            <p style={{ margin: '6px 0 0', color: 'var(--ink-3)', fontSize: 12.5 }}>Loading beneficiaries…</p>
          ) : beneficiaries.length === 0 ? (
            <p style={{ margin: '6px 0 0', color: 'var(--ink-3)', fontSize: 12.5, lineHeight: 1.5 }}>
              No active beneficiaries on this trust yet. Add one on the
              Beneficiaries tab before issuing a certificate.
            </p>
          ) : (
            <select
              data-testid="new-cert-beneficiary"
              value={values.beneficiaryId}
              onChange={(e) => set('beneficiaryId', e.target.value)}
              onBlur={() => touch('beneficiaryId')}
              style={{
                ...certFieldStyle, marginTop: 6,
                ...(err('beneficiaryId') ? { borderColor: 'var(--negative)' } : {}),
              }}
            >
              <option value="">Select a beneficiary…</option>
              {beneficiaries.map((b) => (
                <option key={b.id} value={b.id}>{displayPersonName(b.displayName)}</option>
              ))}
            </select>
          )}
          <FieldError msg={err('beneficiaryId')} />
        </label>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <label style={{ flex: '1 1 160px' }}>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 500 }}>Certificate type</span>
            <select
              data-testid="new-cert-type"
              value={values.certificateType}
              onChange={(e) => set('certificateType', e.target.value as TaxCertificateType)}
              style={{ ...certFieldStyle, marginTop: 6 }}
            >
              <option value="IT3(t)">IT3(t)</option>
              <option value="IT3(b)">IT3(b)</option>
            </select>
          </label>
          <label style={{ flex: '1 1 120px' }}>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 500 }}>Tax year</span>
            <input
              type="text"
              inputMode="numeric"
              data-testid="new-cert-tax-year"
              placeholder={String(currentTaxYear())}
              value={values.taxYear}
              onChange={(e) => set('taxYear', e.target.value)}
              onBlur={() => touch('taxYear')}
              style={{
                ...certFieldStyle, marginTop: 6, fontFamily: 'var(--mono)',
                ...(err('taxYear') ? { borderColor: 'var(--negative)' } : {}),
              }}
            />
            <FieldError msg={err('taxYear')} />
          </label>
        </div>

        <label style={{ display: 'block', marginBottom: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 500 }}>Amount (whole Rand)</span>
          <input
            type="text"
            inputMode="numeric"
            data-testid="new-cert-amount"
            placeholder="30000"
            value={values.amountRand}
            onChange={(e) => set('amountRand', e.target.value)}
            onBlur={() => touch('amountRand')}
            style={{
              ...certFieldStyle, marginTop: 6, fontFamily: 'var(--mono)',
              ...(err('amountRand') ? { borderColor: 'var(--negative)' } : {}),
            }}
          />
          {amountNum !== null && (
            <div className="mono" style={{ marginTop: 4, fontSize: 12, color: 'var(--positive)', fontVariantNumeric: 'tabular-nums' }}>
              = {fmtR(amountNum)}
            </div>
          )}
          <FieldError msg={err('amountRand')} />
        </label>

        {create.isError && !create.error?.isStepUpRequired ? (
          <p
            role="alert"
            data-testid="new-cert-error"
            style={{ fontSize: 12.5, color: 'var(--negative)', marginTop: 10, lineHeight: 1.6 }}
          >
            {mapError(create.error, 'save')}
          </p>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            data-testid="new-cert-submit"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Issuing…' : 'Issue certificate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AFS financials summary (handoff B3 — AFS-GRADING findings 1 + 2) ───────
// The confirmed annual-financial-statements record for this trust — the
// figures SARS s7/s25B attribution starts from, so they sit on the same
// screen as the certificates they back. `useTrust`'s own tier projection
// already omits the field entirely for a caller who may not read the
// trust's `financials` surface (a beneficiary — trusts-get.ts's gate); when
// the surface IS readable but nothing has ever been confirmed, the backend
// sends `null` and this renders nothing. Either way: no empty-scaffold card.
function AfsFigureRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: '10px 0',
        borderBottom: '1px solid var(--hairline)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{label}</span>
      <span
        className="num"
        style={{ fontSize: 14, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  );
}

function AfsFinancialsSummary({ afs }: { afs: NonNullable<TrustSummary['afsFinancials']> }) {
  // Same helper + granularity the dashboard hero uses for this exact field
  // (`afsFinancials.periodEndDate`) — one honest as-at rendering, everywhere.
  const asAt = afs.periodEndDate ? formatAsAtShort(afs.periodEndDate) : null;
  const rows: Array<{ label: string; value: number }> = [
    { label: 'Net asset value', value: afs.netAssetValue },
    { label: 'Total income', value: afs.totalIncome },
    { label: 'Total expenses', value: afs.totalExpenses },
    { label: 'Distributed to beneficiaries', value: afs.distributionsToBeneficiaries },
    { label: 'Total assets', value: afs.totalAssets },
    { label: 'Total liabilities', value: afs.totalLiabilities },
  ].filter((r): r is { label: string; value: number } => r.value !== undefined);

  // A confirmed record with no readable figures at all (only a period end
  // date survived extraction) — still nothing worth a card of empty rows.
  if (rows.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 24 }} data-testid="afs-financials-summary">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        {/* Roman and unpunctuated: a card title is a heading. */}
        <h3 className="trust-card-title">Confirmed AFS figures</h3>
        {asAt ? <span className="card-meta">as at {asAt}</span> : null}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '6px 0 4px', lineHeight: 1.5 }}>
        From the trust&rsquo;s confirmed annual financial statements. These are the figures
        SARS attribution starts from.
      </p>
      <div>
        {rows.map((r) => (
          <AfsFigureRow key={r.label} label={r.label} value={fmtR(r.value)} />
        ))}
      </div>
    </div>
  );
}

function TaxScreen() {
  const { id } = useParams({ from: '/app/trusts/$id/tax' });
  // Handoff B3 — the confirmed AFS record backs the "Confirmed AFS figures"
  // panel below. Reuses the same per-trust detail query the overview screen
  // already caches; the tier gate lives server-side (see AfsFinancialsSummary's
  // own note), so no client-side surface check is needed here.
  const trustQ = useTrust(id);
  const afsFinancials = trustQ.data?.trust.afsFinancials;
  const certsQuery = useTrustTaxCertificates({ trustId: id });
  const certificates = certsQuery.data?.certificates ?? [];
  // A beneficiary reads only the certificates issued to THEM (`compliance-own`);
  // a trustee reads the trust's whole register (`compliance`). The backend says
  // which, and the heading must follow — an own-scoped list under "issued by
  // this trust to its beneficiaries" would imply the trust issued exactly one.
  const ownScope = certsQuery.data?.scope === 'own';

  // "Issue tax certificate" — see TAX_CERTIFICATE_ROLES above for why the role
  // set mirrors the backend's isFirmStaffMember gate. useCanActOnTrust closes
  // the same beneficiary-tier write surface the rest of the app closes.
  const { role } = useSession();
  const canAct = useCanActOnTrust(id);
  const canIssue = TAX_CERTIFICATE_ROLES.has(role) && canAct;
  const [issuing, setIssuing] = useState(false);

  return (
    <ScreenShell
      activeNav="trusts"
      eyebrow={<>Tax &middot; SARS section 7 &amp; section 25B</>}
      /* Roman, unpunctuated: a heading, not a named thing. */
      title={ownScope ? 'Your tax position' : 'Tax position'}
      sub={
        ownScope
          ? 'Tax certificates this trust issued in your name (IT3(t) / IT3(b)).'
          : 'Tax certificates issued by this trust to its beneficiaries (IT3(t) / IT3(b)).'
      }
      topbarRight={
        canIssue ? (
          <button
            type="button"
            className="btn-primary"
            data-testid="issue-tax-certificate-open"
            onClick={() => setIssuing(true)}
          >
            Issue tax certificate
          </button>
        ) : undefined
      }
    >
      <TrustTabs trustId={id} activeTab="tax" />

      {issuing && canIssue && (
        <IssueTaxCertificateModal trustId={id} onClose={() => setIssuing(false)} />
      )}

      {/* The same SARS dates these two panels used to state as prose, now
          counted down against today. See lib/statutory-calendar.ts. */}
      <StatutoryCalendarCard count={4} />

      {afsFinancials ? <AfsFinancialsSummary afs={afsFinancials} /> : null}

      <ConsoleBand
        id="tax-certificates"
        title={ownScope ? 'Your tax certificates' : 'Tax certificates'}
        note={
          certsQuery.data
            ? `${certificates.length} document${certificates.length === 1 ? '' : 's'}`
            : undefined
        }
      >
        {certsQuery.isError ? (
          <StateCard
            variant="error"
            testId="tax-certificates-error"
            label="Tax certificates"
            headline="We couldn’t load tax certificates for this trust."
            detail="Please retry, or contact your administrator."
          />
        ) : certsQuery.data && certificates.length === 0 ? (
          <StateCard
            variant="empty"
            testId="tax-certificates-empty"
            label="Tax certificates"
            headline={
              ownScope
                ? 'No tax certificates in your name yet.'
                : 'No tax certificates issued yet.'
            }
            detail={
              ownScope
                ? 'Certificates appear here once the trust issues an IT3(t) or IT3(b) to you. It may have issued certificates to other beneficiaries.'
                : 'Certificates appear here once IT3(t) or IT3(b) records are created for the trust’s beneficiaries.'
            }
            {...(canIssue && !ownScope
              ? { action: { label: 'Issue a tax certificate', onClick: () => setIssuing(true) } }
              : {})}
          />
        ) : (
          /* `aria-busy` belongs on the CONTAINER while the cell skeletons are
             up: a screen-reader user gets one polite announcement, not five
             shimmering rectangles per row. That is the same rule the shared
             `Skeleton` states, and it is why the bars themselves are
             `aria-hidden`. */
          <div
            className="card trust-table-card trust-table-card--tax"
            {...(certsQuery.isLoading ? { 'aria-busy': true } : {})}
          >
            <BleedTable
              testId="tax-certificates"
              label={
                ownScope
                  ? 'Tax certificates issued to you by this trust'
                  : 'Tax certificates issued by this trust to its beneficiaries'
              }
              columns={CERT_COLUMNS}
              rows={
                certsQuery.isLoading
                  ? [certSkeletonRow(1), certSkeletonRow(2), certSkeletonRow(3)]
                  : certificates.map((cert) => certRow(cert))
              }
            />
          </div>
        )}
      </ConsoleBand>

      <div
        style={{
          marginTop: 28,
          padding: '18px 22px',
          background: 'var(--surface)',
          border: '1px solid var(--hairline)',
          borderRadius: 'var(--r-md)',
          fontSize: 13.5,
          color: 'var(--ink-3)',
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: 'var(--ink-2)' }}>Note:</strong> Tax certificates
        are issued by the trust to its beneficiaries. The trust&rsquo;s own
        ITR12T return is filed separately on SARS eFiling. Your trust
        specialist or tax practitioner should review attribution figures
        before filing.
      </div>
    </ScreenShell>
  );
}
